/**
 * Repost Sheet — bottom sheet for reposting with optional caption.
 * Shows original post preview, text input for user's commentary.
 */

import { useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Repeat2, Send } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useRepost } from '@turnifly/shared/hooks'
import type { FeedItem } from '@turnifly/shared/types'

interface RepostSheetProps {
  post: FeedItem | null
  onClose: () => void
  onReposted?: () => void
}

const MAX_CAPTION = 300

export const RepostSheet = memo(function RepostSheet({
  post,
  onClose,
  onReposted,
}: RepostSheetProps): React.ReactElement | null {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const userId = user?.id
  const repostMutation = useRepost()
  const [caption, setCaption] = useState('')

  const handleSubmit = useCallback((): void => {
    if (!userId || !post) return
    repostMutation.mutate(
      {
        userId,
        originalPostId: post.post_id,
        caption: caption.trim() || undefined,
      },
      {
        onSuccess: () => {
          setCaption('')
          onClose()
          onReposted?.()
        },
      },
    )
  }, [userId, post, caption, repostMutation, onClose])

  if (!post) return null

  const isSubmitting = repostMutation.isPending

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[51] bg-[var(--color-bg)] rounded-t-[28px] max-w-lg mx-auto flex flex-col"
        style={{ maxHeight: '70vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 shrink-0">
          <div className="w-10 h-1 bg-white/[0.12] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-3 shrink-0">
          <button onClick={onClose} className="text-[14px] text-text-hint tap-highlight-transparent">
            {t('common.cancel')}
          </button>
          <div className="flex items-center gap-1.5 text-text-hint/60">
            <Repeat2 size={16} />
            <span className="text-[14px] font-semibold">{t('feed.repost.title')}</span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold transition-all tap-highlight-transparent ${
              !isSubmitting
                ? 'bg-primary text-white shadow-sm shadow-primary/20'
                : 'bg-white/[0.04] text-text-hint/25'
            }`}
          >
            {isSubmitting
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Send size={13} />
            }
            {t('feed.repost.share')}
          </button>
        </div>

        {/* Caption input */}
        <div className="px-5 pb-3 shrink-0">
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
            placeholder={t('feed.repost.placeholder')}
            rows={3}
            className="w-full bg-transparent text-[14px] text-text placeholder:text-text-hint/25 resize-none focus:outline-none leading-relaxed"
          />
          {caption.length > MAX_CAPTION * 0.8 && (
            <p className={`text-right text-[11px] tabular-nums ${
              caption.length >= MAX_CAPTION ? 'text-red-400' : 'text-text-hint/30'
            }`}>
              {caption.length}/{MAX_CAPTION}
            </p>
          )}
        </div>

        {/* Original post preview */}
        <div className="mx-5 mb-5 border border-white/[0.06] rounded-xl p-3 overflow-y-auto max-h-[200px]">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full overflow-hidden bg-white/[0.06] shrink-0">
              {post.author_avatar ? (
                <img src={post.author_avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] text-text-hint/50 font-semibold">
                  {(post.author_name ?? '?')[0]}
                </div>
              )}
            </div>
            <span className="text-[12px] font-medium text-text-hint/60">{post.author_name}</span>
          </div>
          {post.content && (
            <p className="text-[13px] text-text/60 line-clamp-4 leading-relaxed">{post.content}</p>
          )}
          {Array.isArray(post.media_urls) && (post.media_urls as string[]).length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {(post.media_urls as string[]).slice(0, 3).map((url, i) => (
                <div key={i} className="w-12 h-12 rounded-lg overflow-hidden bg-white/[0.04]">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </>
  )
})
